import { cn } from "@tcg/ui";
import { useLiveQuery } from "dexie-react-hooks";
import { ScanLine, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { LanguagePicker } from "@/components/LanguagePicker";
import { NumPad } from "@/components/NumPad";
import type { LockReason, Staff } from "@/lib/auth";
import { readSetting, staffLocaleKey, writeSetting } from "@/lib/db";
import { errorText } from "@/lib/errors";
import { isPosLocale, type PosLocale } from "@/i18n";

interface Who {
  staffId?: string;
  badge?: string;
  name: string;
}

export interface PinScreenProps {
  reason: LockReason;
  lastStaff: Staff | null;
}

const REASON_KEY: Partial<Record<LockReason, string>> = {
  idle: "pin.locked_idle",
  expired: "pin.locked_expired",
  signed_out: "pin.locked_signed_out",
};

/**
 * Staff sign-in with the PIN (T07-22): big name buttons of the people who used this tablet, or
 * "someone else" → scan the staff card (the scanner types into the field) / type the e-mail; then
 * the PIN on the number pad. The UI language switches to the staff member's — unless someone picked
 * a language on this screen just now (the picker, top right): that one stays and becomes the
 * choice of whoever signs in.
 */
export function PinScreen({ reason, lastStaff }: PinScreenProps) {
  const { t, i18n } = useTranslation();
  const { auth, db } = usePos();
  const recent = useLiveQuery(
    () => db.staffRecent.orderBy("lastSeenAt").reverse().limit(12).toArray(),
    [db],
  );
  const [who, setWhoState] = useState<Who | null>(
    lastStaff && reason !== "signed_out" ? { staffId: lastStaff.id, name: lastStaff.name } : null,
  );
  const [other, setOther] = useState(false);
  const [badge, setBadge] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<PosLocale | null>(null);

  const setWho = (next: Who | null) => {
    setWhoState(next);
    setPin("");
  };

  const submit = async () => {
    if (!who || pin.length < 4 || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const staff = await auth.signIn({
        ...(who.staffId ? { staffId: who.staffId } : { badge: who.badge ?? "" }),
        pin,
      });
      if (picked) {
        // chosen on this screen before signing in — keep it as this person's choice here
        await writeSetting(db, staffLocaleKey(staff.id), picked);
        return;
      }
      // the language this staff member chose on this tablet (設定, T07-34) wins over the profile
      const chosen = await readSetting<string>(db, staffLocaleKey(staff.id), "");
      const locale = isPosLocale(chosen) ? chosen : staff.locale;
      if (isPosLocale(locale) && locale !== i18n.resolvedLanguage) {
        void i18n.changeLanguage(locale);
      }
    } catch (err) {
      setPin("");
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const notice = REASON_KEY[reason];
  return (
    <div className="grid h-full grid-cols-1 gap-6 p-6 md:grid-cols-[1fr_24rem]">
      <section aria-labelledby="pin-title" className="flex min-h-0 flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 id="pin-title" className="text-3xl font-semibold">
            {t("pin.title")}
          </h1>
          <LanguagePicker onChange={setPicked} />
        </div>
        {notice ? (
          <p role="status" className="rounded-md border bg-accent px-4 py-3 text-lg">
            {t(notice)}
          </p>
        ) : null}
        <p className="text-muted-foreground">{t("pin.choose_staff")}</p>
        <div className="grid grid-cols-2 gap-3 overflow-y-auto lg:grid-cols-3">
          {(recent ?? []).map((person) => (
            <button
              key={person.id}
              type="button"
              aria-pressed={who?.staffId === person.id}
              onClick={() => {
                setOther(false);
                setWho({ staffId: person.id, name: person.name });
              }}
              className={cn(
                "flex min-h-20 items-center gap-3 rounded-lg border px-4 text-left text-xl",
                who?.staffId === person.id && "border-primary bg-accent ring-2 ring-primary",
              )}
            >
              <UserRound aria-hidden className="size-8 shrink-0" />
              <span className="truncate">{person.name}</span>
            </button>
          ))}
          <button
            type="button"
            aria-pressed={other}
            onClick={() => {
              setOther(true);
              setWho(null);
            }}
            className={cn(
              "flex min-h-20 items-center gap-3 rounded-lg border border-dashed px-4 text-left text-xl",
              other && "border-primary bg-accent",
            )}
          >
            <ScanLine aria-hidden className="size-8 shrink-0" />
            {t("pin.other_staff")}
          </button>
        </div>
        {(recent ?? []).length === 0 && !other ? (
          <p className="text-muted-foreground">{t("pin.no_staff_yet")}</p>
        ) : null}
        {other ? (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (badge.trim()) {
                setWho({ badge: badge.trim(), name: badge.trim() });
              }
            }}
          >
            <label className="flex flex-col gap-1">
              <span className="font-medium">{t("pin.badge_label")}</span>
              <input
                autoFocus
                value={badge}
                onChange={(e) => setBadge(e.target.value)}
                onBlur={() => badge.trim() && setWho({ badge: badge.trim(), name: badge.trim() })}
                placeholder={t("pin.badge_placeholder")}
                autoComplete="off"
                className="min-h-pos-touch rounded-md border px-3 text-lg"
              />
            </label>
          </form>
        ) : null}
      </section>
      <section aria-label={t("pin.pin_label")} className="flex flex-col justify-center gap-3">
        <p className="text-xl font-medium">
          {who ? t("pin.pin_for", { name: who.name }) : t("pin.pin_label")}
        </p>
        <NumPad
          label={t("pin.pin_label")}
          value={pin}
          onChange={setPin}
          maxLength={6}
          masked
          disabled={!who || busy}
          onEnter={() => void submit()}
          enterLabel={busy ? t("app.saving") : t("pin.submit")}
        />
        {error ? (
          <p role="alert" className="text-lg text-pos-problem">
            {error}
          </p>
        ) : null}
      </section>
    </div>
  );
}
